import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useHistory } from "react-router-dom"
import { defineMessages } from "react-intl"
import _ from "lodash"
import Box from "@material-ui/core/Box"
import { makeStyles } from "@material-ui/core/styles"
import useConfirmDialog from "../../component/dialog/useConfirmDialog"

import { checkUserType } from "../../component/userUtils"
import UserForm from "./UserForm"
import users from "../../api/userSlice"
import { actions as notifier } from "../../component/notifier"
import CircularProgressBox from "../../component/CircularProgressBox"
import { dialogMessages, words } from "../../i18n/lstrings"
import useSmartFormat from "../../i18n/useSmartFormat"
import httpClient, { CancelToken } from "../../api/httpClient"
import { NORMAL, STAFF } from "../../commons/userTypes"

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4)
  }
}))

const msg = defineMessages({
  saveAdminError: {
    id: "user.save.error.admin",
    defaultMessage: "You cannot remove your administrator privileges"
  },
  saveSuccess: {
    id: "user.save.message",
    defaultMessage: "User Saved"
  },
  saveError: {
    id: "user.save.error.exist",
    defaultMessage: "An user already exists with this account"
  },
  resetPasswordDialogTitle: {
    id: "user.reset_password_dialog.title",
    defaultMessage: "Password reset"
  },
  resetPasswordDialogContent: {
    id: "user.reset_password_dialog.content",
    defaultMessage:
      "Are you sure you want to reset the password of {firstName} {lastName} (email: {email}) ?"
  }
})

const emptyObj = {}

// ## //
const AddOrEditUserPage = () => {
  const classes = useStyles()
  const history = useHistory()
  const { id } = useParams()
  const format = useSmartFormat()

  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  const initialValues = useSelector((state) =>
    id && state.user.selected && id === state.user.selected._id
      ? state.user.selected
      : emptyObj
  )

  const resetPasswordConfirm = useConfirmDialog(
    "resetPassword",
    (state) => {
      if (state.resetPassword) {
        const values = { email: state.resetPassword.email }
        httpClient
          .post("/auth/admin/forgotpassword", values)
          .then(() => {
            const message = format(dialogMessages.forgotPasswordSuccess, values)
            dispatch(notifier.enqueueSuccess(message))
          })
          .catch((err) => {
            const errMsg =
              err.type === 404
                ? dialogMessages.forgotPasswordInvalidEmail
                : dialogMessages.forgotPasswordError
            dispatch(notifier.enqueueWarning(errMsg))
          })
          .finally(() => history.goBack())
      }
    },
    {
      title: msg.resetPasswordDialogTitle,
      message: (data) => format(msg.resetPasswordDialogContent, data)
    }
  )

  const { actions } = users
  const permission = checkUserType(user, STAFF) ? "admin" : "guest"
  const redirectRule = checkUserType(user, STAFF) ? "/admin/users" : "/"

  useEffect(() => {
    const { token, cancel } = CancelToken.source()
    if (id) {
      dispatch(actions.fetchUserById(id, token))
    }
    return () => cancel()
  }, [actions, dispatch, id])

  const handleSave = (values) => {
    // Safety check to avoid removing last staff member
    if (
      !checkUserType(values, STAFF) &&
      values._id === user._id &&
      checkUserType(user, STAFF)
    ) {
      dispatch(notifier.enqueueError(msg.saveAdminError))
      return
    }

    // convert Administrator checkbox into its value
    //  > checked   = staff
    //  > unchecked = normal
    const type = values.staff ? STAFF : NORMAL
    const toSaveUser = {
      _id: values._id,
      username: values.username,
      firstName: values.firstName,
      lastName: values.lastName,
      primaryEmail: values.primaryEmail,
      status: values.status,
      profile: values.profile,
      type
    }
    dispatch(actions.saveOrUpdate(toSaveUser))
      .then(() => {
        dispatch(notifier.enqueueSuccess(msg.saveSuccess))
        history.push(redirectRule)
      })
      .catch((error) => {
        if (error.type === 409) {
          dispatch(notifier.enqueueError(msg.saveError))
        }
      })
  }

  const handleCancel = (event) => {
    event.preventDefault()
    history.goBack()
  }

  const handlePassword = (event) => {
    event.preventDefault()
    if (initialValues && initialValues._id) {
      history.push(`/change-password/${initialValues._id}`)
    } else {
      history.push("/change-password")
    }
  }

  const handleResetPassword = (event) => {
    event.preventDefault()
    const { firstName, lastName, primaryEmail } = initialValues
    resetPasswordConfirm({ firstName, lastName, email: primaryEmail })
  }

  const headingMessage = (initValues) => {
    if (initValues && initValues._id) {
      return format(words.edit)
    }

    return format(words.add)
  }

  if (id && _.isEmpty(initialValues)) {
    return <CircularProgressBox height="80vh" />
  }

  return (
    <Box className={classes.root}>
      <UserForm
        handleCancel={handleCancel}
        handleSave={handleSave}
        heading={headingMessage(initialValues)}
        initialValues={initialValues}
        handlePassword={handlePassword}
        handleResetPassword={handleResetPassword}
        permissions={permission}
      />
    </Box>
  )
}

export default AddOrEditUserPage
